/**
 * Predefined SMART goal packs for recreational (loisir) players.
 * Must stay in sync with Backend/src/config/leisureSmartPresets.js
 */
import {
  PURPOSE_RECREATIONAL,
  EXPERIENCE_BEGINNER,
  EXPERIENCE_INTERMEDIATE,
  EXPERIENCE_ADVANCED,
} from "./profilingFocusCatalog";

export const DEFAULT_FOCUS_ID = 3;

export const LEISURE_DISPLAY_LABELS = {
  en: {
    1: "Learn the basics",
    3: "Just have fun",
    4: "Play with friends",
    7: "Get better at serving",
    10: "Keep the ball in play",
    19: "Unwind and relax",
  },
  fr: {
    1: "Apprendre les bases",
    3: "Juste m'amuser",
    4: "Jouer avec des amis",
    7: "Bien servir",
    10: "Garder la balle en jeu",
    19: "Décompresser",
  },
  es: {
    1: "Aprender las bases",
    3: "Solo divertirme",
    4: "Jugar con amigos",
    7: "Mejorar el saque",
    10: "Mantener la pelota en juego",
    19: "Desconectar y relajarme",
  },
  de: {
    1: "Die Basics lernen",
    3: "Einfach Spaß haben",
    4: "Mit Freunden spielen",
    7: "Besser aufschlagen",
    10: "Den Ball im Spiel halten",
    19: "Abschalten und entspannen",
  },
  nl: {
    1: "De basis leren",
    3: "Gewoon plezier hebben",
    4: "Spelen met vrienden",
    7: "Beter serveren",
    10: "De bal in het spel houden",
    19: "Ontspannen en uitrusten",
  },
  pt: {
    1: "Aprender as bases",
    3: "Só divertir-me",
    4: "Jogar com amigos",
    7: "Melhorar o serviço",
    10: "Manter a bola em jogo",
    19: "Descontrair e relaxar",
  },
};

export const LEISURE_BEGINNER_VISIBLE_FOCUS_IDS = [3, 4, 19, 1, 10];

function langKey(lang) {
  const raw = String(lang || "en").toLowerCase().split("-")[0];
  if (raw === "gr" || raw === "ge") return "de";
  if (raw === "sp") return "es";
  if (["en", "fr", "es", "de", "nl", "pt"].includes(raw)) return raw;
  return "en";
}

function levelBand(experienceLevelId) {
  const id = Number(experienceLevelId);
  if (id === EXPERIENCE_ADVANCED) return "advanced";
  if (id === EXPERIENCE_INTERMEDIATE) return "intermediate";
  return "beginner";
}

const PRESET_TEMPLATES = {
  3: {
    questTitle: {
      en: "Fun Mission",
      fr: "Mission Plaisir",
      es: "Misión diversión",
      de: "Spaß-Mission",
      nl: "Fun-missie",
      pt: "Missão diversão",
    },
    S: {
      en: "Have fun and keep the ball in play with my partners",
      fr: "M'amuser et garder la balle en jeu avec mes partenaires",
      es: "Divertirme y mantener la pelota en juego con mis compañeros",
      de: "Spaß haben und den Ball mit Partnern im Spiel halten",
      nl: "Plezier hebben en de bal met partners in het spel houden",
      pt: "Divertir-me e manter a bola em jogo com os meus parceiros",
    },
    M: {
      metricKind: "fun_rating",
      en: "Sessions per week + fun rating (1–5) after each session",
      fr: "Séances par semaine + note fun (1–5) après chaque séance",
      es: "Sesiones por semana + nota fun (1–5) tras cada sesión",
      de: "Einheiten pro Woche + Fun-Note (1–5) nach jeder Einheit",
      nl: "Sessies per week + fun-score (1–5) na elke sessie",
      pt: "Sessões por semana + nota fun (1–5) após cada sessão",
    },
    A: {
      milestoneId: "break_rally",
      horizon: "mid",
      en: "Beat my record: 10 rallies in a row",
      fr: "Battre mon record : 10 échanges d'affilée",
      es: "Batir mi récord: 10 peloteos seguidos",
      de: "Meinen Rekord knacken: 10 Ballwechsel in Folge",
      nl: "Mijn record breken: 10 rally's achter elkaar",
      pt: "Bater o meu recorde: 10 trocas seguidas",
    },
    R: {
      chipId: "fun",
      en: "It helps me relax and enjoy myself",
      fr: "Ça me détend et me fait du bien",
      es: "Me ayuda a relajarme y disfrutar",
      de: "Es hilft mir, abzuschalten und Spaß zu haben",
      nl: "Het helpt me ontspannen en genieten",
      pt: "Ajuda-me a relaxar e a divertir-me",
    },
    suggestedExerciseTags: ["fun", "beginner", "rally"],
  },
  4: {
    questTitle: {
      en: "Club Friends",
      fr: "Copains du club",
      es: "Amigos del club",
      de: "Club-Freunde",
      nl: "Clubvrienden",
      pt: "Amigos do clube",
    },
    S: {
      en: "Play regularly with club friends and enjoy the social side",
      fr: "Jouer régulièrement avec les copains du club",
      es: "Jugar regularmente con amigos del club y disfrutar lo social",
      de: "Regelmäßig mit Club-Freunden spielen und den sozialen Teil genießen",
      nl: "Regelmatig spelen met clubvrienden en van de sociale kant genieten",
      pt: "Jogar regularmente com amigos do clube e aproveitar o lado social",
    },
    M: {
      metricKind: "sessions_per_week",
      en: "Show up for at least one club session per week",
      fr: "Venir au moins une fois par semaine au club",
      es: "Venir al menos a una sesión de club por semana",
      de: "Mindestens eine Club-Einheit pro Woche",
      nl: "Minstens één clubsessie per week",
      pt: "Ir a pelo menos uma sessão de clube por semana",
    },
    A: {
      milestoneId: "club_months",
      horizon: "mid",
      en: "Play with someone new at the club this month",
      fr: "Jouer avec quelqu'un de nouveau au club ce mois-ci",
      es: "Jugar con alguien nuevo en el club este mes",
      de: "Diesen Monat mit jemandem Neuem im Club spielen",
      nl: "Deze maand met iemand nieuws in de club spelen",
      pt: "Jogar com alguém novo no clube este mês",
    },
    R: {
      chipId: "community",
      en: "I love being part of the club",
      fr: "J'aime faire partie du club",
      es: "Me encanta formar parte del club",
      de: "Ich gehöre gerne zum Club",
      nl: "Ik vind het fijn om bij de club te horen",
      pt: "Gosto de fazer parte do clube",
    },
    suggestedExerciseTags: ["fun", "social", "beginner"],
  },
  19: {
    questTitle: {
      en: "Stress-Free",
      fr: "Zéro stress",
      es: "Cero estrés",
      de: "Null Stress",
      nl: "Geen stress",
      pt: "Zero stress",
    },
    S: {
      en: "Use table tennis to unwind and clear my head",
      fr: "Utiliser le ping pour décompresser et vider la tête",
      es: "Usar el ping para desconectar y despejar la mente",
      de: "Tischtennis zum Abschalten und Kopf frei bekommen nutzen",
      nl: "Tafeltennis gebruiken om te ontspannen en mijn hoofd leeg te maken",
      pt: "Usar o pingue-pongue para descontrair e limpar a cabeça",
    },
    M: {
      metricKind: "fun_rating",
      en: "Fun rating (1–5) after each relaxed session",
      fr: "Note fun (1–5) après chaque séance détente",
      es: "Nota fun (1–5) tras cada sesión relajada",
      de: "Fun-Note (1–5) nach jeder lockeren Einheit",
      nl: "Fun-score (1–5) na elke ontspannen sessie",
      pt: "Nota fun (1–5) após cada sessão relaxada",
    },
    A: {
      milestoneId: "weekly_habit",
      horizon: "mid",
      en: "Play 3 relaxed sessions without pressure",
      fr: "Faire 3 séances détente sans pression",
      es: "Hacer 3 sesiones relajadas sin presión",
      de: "3 lockere Einheiten ohne Druck spielen",
      nl: "3 ontspannen sessies zonder druk spelen",
      pt: "Fazer 3 sessões relaxadas sem pressão",
    },
    R: {
      chipId: "health",
      en: "It helps me switch off after work",
      fr: "Ça m'aide à déconnecter après le boulot",
      es: "Me ayuda a desconectar después del trabajo",
      de: "Es hilft mir, nach der Arbeit abzuschalten",
      nl: "Het helpt me af te schakelen na het werk",
      pt: "Ajuda-me a desligar depois do trabalho",
    },
    suggestedExerciseTags: ["fun", "relax", "beginner"],
  },
  1: {
    questTitle: {
      en: "Basics Quest",
      fr: "Quête des bases",
      es: "Quest de las bases",
      de: "Basics-Quest",
      nl: "Basis-quest",
      pt: "Quest das bases",
    },
    S: {
      en: "Learn a few simple shots and enjoy hitting the ball",
      fr: "Apprendre quelques coups simples et prendre plaisir à frapper la balle",
      es: "Aprender unos golpes simples y disfrutar golpeando la pelota",
      de: "Ein paar einfache Schläge lernen und Spaß am Ballhaben",
      nl: "Een paar eenvoudige slagen leren en genieten van de bal raken",
      pt: "Aprender alguns golpes simples e gostar de bater na bola",
    },
    M: {
      metricKind: "rally_record",
      en: "Track my longest rally each session",
      fr: "Noter mon plus long échange à chaque séance",
      es: "Anotar mi peloteo más largo en cada sesión",
      de: "Meinen längsten Ballwechsel pro Einheit notieren",
      nl: "Mijn langste rally per sessie bijhouden",
      pt: "Registar a minha troca mais longa em cada sessão",
    },
    A: {
      milestoneId: "break_rally",
      horizon: "mid",
      en: "Reach 10 rallies in a row",
      fr: "Atteindre 10 échanges d'affilée",
      es: "Llegar a 10 peloteos seguidos",
      de: "10 Ballwechsel in Folge erreichen",
      nl: "10 rally's achter elkaar halen",
      pt: "Chegar a 10 trocas seguidas",
    },
    R: {
      chipId: "fun",
      en: "Learning feels good and keeps it fun",
      fr: "Apprendre, c'est fun et ça me motive",
      es: "Aprender se siente bien y mantiene la diversión",
      de: "Lernen fühlt sich gut an und bleibt spaßig",
      nl: "Leren voelt goed en houdt het leuk",
      pt: "Aprender faz bem e mantém a diversão",
    },
    suggestedExerciseTags: ["fun", "beginner", "basics"],
  },
  7: {
    questTitle: {
      en: "Serve Challenge",
      fr: "Défi service",
      es: "Challenge de saque",
      de: "Aufschlag-Challenge",
      nl: "Serve-challenge",
      pt: "Challenge de serviço",
    },
    S: {
      en: "Get comfortable serving and starting a point",
      fr: "Me sentir à l'aise pour servir et commencer un point",
      es: "Sentirme cómodo sacando y empezando el punto",
      de: "Sicher aufschlagen und den Punkt starten",
      nl: "Comfortabel serveren en een punt beginnen",
      pt: "Sentir-me à vontade a servir e a começar o ponto",
    },
    M: {
      metricKind: "sessions_per_week",
      en: "Practice serves for 5 minutes at each session",
      fr: "5 minutes de service à chaque séance",
      es: "Practicar saques 5 minutos en cada sesión",
      de: "5 Minuten Aufschlagtraining pro Einheit",
      nl: "5 minuten serves oefenen per sessie",
      pt: "Praticar serviços 5 minutos em cada sessão",
    },
    A: {
      milestoneId: "master_serve",
      horizon: "mid",
      en: "Land 5 serves on the table in a row",
      fr: "Réussir 5 services sur la table d'affilée",
      es: "Meter 5 saques en la mesa seguidos",
      de: "5 Aufschläge hintereinander auf dem Tisch landen",
      nl: "5 serves achter elkaar op tafel krijgen",
      pt: "Acertar 5 serviços na mesa seguidos",
    },
    R: {
      chipId: "confidence",
      en: "Serving well makes me feel more confident",
      fr: "Bien servir me donne confiance",
      es: "Sacar bien me da más confianza",
      de: "Gutes Aufschlagen gibt mir mehr Selbstvertrauen",
      nl: "Goed serveren geeft me meer vertrouwen",
      pt: "Servir bem dá-me mais confiança",
    },
    suggestedExerciseTags: ["fun", "beginner", "serve"],
  },
  10: {
    questTitle: {
      en: "Keep It Going",
      fr: "La balle en jeu",
      es: "Mantenerla en juego",
      de: "Ball im Spiel",
      nl: "De bal in het spel",
      pt: "Manter a bola em jogo",
    },
    S: {
      en: "Keep the ball in play longer and enjoy longer rallies",
      fr: "Garder la balle en jeu plus longtemps",
      es: "Mantener la pelota en juego más tiempo y disfrutar peloteos largos",
      de: "Den Ball länger im Spiel halten und längere Ballwechsel genießen",
      nl: "De bal langer in het spel houden en genieten van langere rally's",
      pt: "Manter a bola em jogo mais tempo e aproveitar trocas mais longas",
    },
    M: {
      metricKind: "rally_record",
      en: "Track my best rally count each week",
      fr: "Noter mon meilleur échange chaque semaine",
      es: "Anotar mi mejor peloteo cada semana",
      de: "Meinen besten Ballwechsel jede Woche notieren",
      nl: "Mijn beste rally elke week bijhouden",
      pt: "Registar a minha melhor troca a cada semana",
    },
    A: {
      milestoneId: "break_rally",
      horizon: "mid",
      en: "Beat my personal rally record",
      fr: "Battre mon record d'échanges",
      es: "Batir mi récord personal de peloteos",
      de: "Meinen persönlichen Ballwechsel-Rekord knacken",
      nl: "Mijn persoonlijke rally-record breken",
      pt: "Bater o meu recorde pessoal de trocas",
    },
    R: {
      chipId: "fun",
      en: "Longer rallies are more fun",
      fr: "Plus d'échanges = plus de fun",
      es: "Los peloteos más largos son más divertidos",
      de: "Längere Ballwechsel machen mehr Spaß",
      nl: "Langere rally's zijn leuker",
      pt: "Trocas mais longas são mais divertidas",
    },
    suggestedExerciseTags: ["fun", "beginner", "consistency"],
  },
};

const LEVEL_TWEAKS = {
  intermediate: {
    serveTarget: {
      en: "8 serves on the table in a row",
      fr: "8 services sur la table d'affilée",
      es: "8 saques en la mesa seguidos",
      de: "8 Aufschläge hintereinander auf dem Tisch",
      nl: "8 serves achter elkaar op tafel",
      pt: "8 serviços na mesa seguidos",
    },
  },
  advanced: {
    serveTarget: {
      en: "10 serves on the table in a row",
      fr: "10 services sur la table d'affilée",
      es: "10 saques en la mesa seguidos",
      de: "10 Aufschläge hintereinander auf dem Tisch",
      nl: "10 serves achter elkaar op tafel",
      pt: "10 serviços na mesa seguidos",
    },
  },
};

/**
 * Forehand rally challenge count for leisure:
 * - never played + children → 5
 * - young / adolescents first time → 10
 * - already played (intermediate+) → 20
 */
export function resolveForehandRallyCount(experienceLevelId, dateOfBirth) {
  const band = levelBand(experienceLevelId);
  if (band === "intermediate" || band === "advanced") return 20;
  const age = ageBandFromDob(dateOfBirth);
  if (age === "child") return 5;
  return 10;
}

function forehandRallyLabels(count) {
  const n = Number(count) || 10;
  return {
    en: `Reach ${n} forehand rallies in a row`,
    fr: `Atteindre ${n} échanges coup droit d'affilée`,
    es: `Llegar a ${n} peloteos de derecha seguidos`,
    de: `${n} Vorhand-Ballwechsel in Folge erreichen`,
    nl: `${n} forehand-rally's achter elkaar halen`,
    pt: `Chegar a ${n} trocas de direita seguidas`,
  };
}

function pickLocalized(obj, lk) {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lk] || obj.en || "";
}

export function resolveLeisureSmartPreset({
  primaryFocusId,
  experienceLevelId,
  dateOfBirth,
  lang,
} = {}) {
  const focusId = Number(primaryFocusId) || DEFAULT_FOCUS_ID;
  const lk = langKey(lang);
  const band = levelBand(experienceLevelId);
  const template = PRESET_TEMPLATES[focusId] || PRESET_TEMPLATES[DEFAULT_FOCUS_ID];
  const tweaks = LEVEL_TWEAKS[band] || null;

  let milestoneLabel = pickLocalized(template.A, lk);
  if (template.A.milestoneId === "break_rally") {
    const count = resolveForehandRallyCount(experienceLevelId, dateOfBirth);
    milestoneLabel = pickLocalized(forehandRallyLabels(count), lk);
  } else if (tweaks && template.A.milestoneId === "master_serve") {
    milestoneLabel = pickLocalized(tweaks.serveTarget, lk);
  }

  const cycleWeeks = 3;
  const questTitle = pickLocalized(template.questTitle, lk);
  const sSummary = pickLocalized(template.S, lk);
  const mLabel = pickLocalized(template.M, lk);
  const whyItMatters = pickLocalized(template.R, lk);

  return {
    primaryFocusId: focusId,
    experienceLevelId: Number(experienceLevelId) || EXPERIENCE_BEGINNER,
    questTitle,
    displayLabel: LEISURE_DISPLAY_LABELS[lk]?.[focusId] || LEISURE_DISPLAY_LABELS.en[focusId],
    S: { summary: sSummary, label: sSummary },
    M: {
      metricKind: template.M.metricKind,
      label: mLabel,
      targetHint: mLabel,
    },
    A: {
      milestoneId: template.A.milestoneId,
      horizon: template.A.horizon,
      label: milestoneLabel,
      rallyCount:
        template.A.milestoneId === "break_rally"
          ? resolveForehandRallyCount(experienceLevelId, dateOfBirth)
          : null,
    },
    R: {
      whyItMatters,
      chipId: template.R.chipId,
    },
    T: {
      defaultCycleWeeks: cycleWeeks,
      // Internal pacing only — not shown as a SMART cycle to leisure players
      reviewHint: null,
    },
    suggestedExerciseTags: [...(template.suggestedExerciseTags || ["fun", "beginner"])],
  };
}

export function isLeisurePurpose(purposeId) {
  return Number(purposeId) === PURPOSE_RECREATIONAL;
}

export function applyLeisurePresetToSlots(
  slots,
  { primaryFocusId, experienceLevelId, dateOfBirth, lang } = {}
) {
  const preset = resolveLeisureSmartPreset({
    primaryFocusId: primaryFocusId ?? slots?.S?.primaryFocusId,
    experienceLevelId: experienceLevelId ?? slots?.M?.experienceLevelId,
    dateOfBirth: dateOfBirth ?? slots?.R?.dateOfBirth,
    lang,
  });
  const next = {
    S: { ...(slots?.S || {}), labels: { ...(slots?.S?.labels || {}) } },
    M: { ...(slots?.M || {}), labels: { ...(slots?.M?.labels || {}) } },
    A: { ...(slots?.A || {}), labels: { ...(slots?.A?.labels || {}) } },
    R: { ...(slots?.R || {}), labels: { ...(slots?.R?.labels || {}) } },
    T: { ...(slots?.T || {}) },
  };

  next.A.milestones = [
    {
      id: preset.A.milestoneId,
      horizon: preset.A.horizon,
      label: preset.A.label,
    },
  ];
  next.A.labels.milestones = preset.A.label;
  next.R.whyItMatters = preset.R.whyItMatters;
  next.R.alignedPurposeId = PURPOSE_RECREATIONAL;
  next.R.motivationSummary = preset.R.whyItMatters;
  next.R.labels.why = preset.R.whyItMatters;
  next.T.cycleWeeks = preset.T.defaultCycleWeeks;
  next.M.metric = {
    kind: preset.M.metricKind,
    label: preset.M.label,
    targetHint: preset.M.targetHint,
  };
  next.M.labels.metric = preset.M.label;

  return { slots: next, preset };
}

export function leisureDisplayLabel(focusId, lang) {
  const lk = langKey(lang);
  return LEISURE_DISPLAY_LABELS[lk]?.[Number(focusId)] || LEISURE_DISPLAY_LABELS.en[Number(focusId)] || "";
}

/** Fun, non-technical labels for leisure experience chips (override DB names). */
export const LEISURE_EXPERIENCE_LABELS = {
  en: {
    1: "I'm just starting",
    2: "I've played a bit already",
    3: "I play often",
  },
  fr: {
    1: "Je débute",
    2: "J'ai déjà un peu joué",
    3: "Je joue souvent",
  },
  es: {
    1: "Estoy empezando",
    2: "Ya he jugado un poco",
    3: "Juego a menudo",
  },
  de: {
    1: "Ich fange gerade an",
    2: "Ich habe schon ein bisschen gespielt",
    3: "Ich spiele oft",
  },
  nl: {
    1: "Ik begin net",
    2: "Ik heb al een beetje gespeeld",
    3: "Ik speel vaak",
  },
  pt: {
    1: "Estou a começar",
    2: "Já joguei um pouco",
    3: "Jogo com frequência",
  },
};

export function leisureExperienceLabel(experienceLevelId, lang) {
  const lk = langKey(lang);
  const id = Number(experienceLevelId);
  return LEISURE_EXPERIENCE_LABELS[lk]?.[id] || LEISURE_EXPERIENCE_LABELS.en[id] || "";
}

/**
 * Age band from DOB (DD/MM/YYYY, YYYY-MM-DD, or Date).
 * @returns {'child'|'teen'|'adult'|null}
 */
export function ageBandFromDob(dob) {
  if (!dob) return null;
  let year;
  let month;
  let day;
  if (dob instanceof Date && !Number.isNaN(dob.getTime())) {
    year = dob.getFullYear();
    month = dob.getMonth() + 1;
    day = dob.getDate();
  } else {
    const s = String(dob).trim();
    let m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m) {
      day = Number(m[1]);
      month = Number(m[2]);
      year = Number(m[3]);
    } else {
      m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return null;
      year = Number(m[1]);
      month = Number(m[2]);
      day = Number(m[3]);
    }
  }
  const born = new Date(year, month - 1, day);
  if (Number.isNaN(born.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - born.getFullYear();
  const md = now.getMonth() - born.getMonth();
  if (md < 0 || (md === 0 && now.getDate() < born.getDate())) age -= 1;
  if (age < 0 || age > 120) return null;
  if (age < 12) return "child";
  if (age < 18) return "teen";
  return "adult";
}

export function leisureVisibleFocusIds(experienceLevelId) {
  const band = levelBand(experienceLevelId);
  if (band === "beginner") return LEISURE_BEGINNER_VISIBLE_FOCUS_IDS;
  return null;
}

export function isLeisureBeginnerOrIntermediate(experienceLevelId) {
  const id = Number(experienceLevelId);
  return id === EXPERIENCE_BEGINNER || id === EXPERIENCE_INTERMEDIATE || !Number.isFinite(id) || id <= 0;
}
